// CDN-backed, Mapbox-free "collapse N-of-N children → parent" pass, driven by boundaryShapeGroup + boundaryShapeId + geometry containment (core centroid / fuzzy bbox-center buckets).

import { ServiceArea } from '../types/serviceArea';
import { getStateFromArea } from '../components/edit_supplier_service_areas_form/utils';
import {
	ensureCachedGeoBoundariesFeatureCollection,
	ensureCachedWorldAdm0FeatureCollection,
} from './geoBoundariesApi';

type LabelFn = (a: ServiceArea) => string;

interface AdmBbox {
	minLng: number;
	minLat: number;
	maxLng: number;
	maxLat: number;
}

// Per-state bucket: `core` = centroid-resident counties (canonical); `fuzzy` ⊇ `core` also includes bbox-center matches. Collapse when `core ⊆ selected ⊆ fuzzy`.
interface Adm1Bucket {
	adm1ShapeId: string;
	adm1Feature: any;
	adm1Bbox: AdmBbox | null;
	coreAdm2ShapeIds: Set<string>;
	fuzzyAdm2ShapeIds: Set<string>;
}

// Minimal bbox for Polygon / MultiPolygon; null on missing / degenerate geometry.
function bboxFromGeometry(geom: any): AdmBbox | null {
	if (!geom || !geom.coordinates) {
		return null;
	}
	let minLng = Infinity;
	let minLat = Infinity;
	let maxLng = -Infinity;
	let maxLat = -Infinity;
	function walk(coords: any): void {
		if (!coords || coords.length === 0) {
			return;
		}
		if (typeof coords[0][0] === 'number') {
			for (let i = 0; i < coords.length; i++) {
				const lng = coords[i][0];
				const lat = coords[i][1];
				if (lng < minLng) minLng = lng;
				if (lng > maxLng) maxLng = lng;
				if (lat < minLat) minLat = lat;
				if (lat > maxLat) maxLat = lat;
			}
			return;
		}
		for (let i = 0; i < coords.length; i++) {
			walk(coords[i]);
		}
	}
	if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
		walk(geom.coordinates);
	} else {
		return null;
	}
	if (minLng > maxLng || minLat > maxLat) {
		return null;
	}
	return { minLng: minLng, minLat: minLat, maxLng: maxLng, maxLat: maxLat };
}

function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
	if (!ring || ring.length < 3) {
		return false;
	}
	let inside = false;
	const n = ring.length;
	for (let i = 0, j = n - 1; i < n; j = i++) {
		const xi = ring[i][0];
		const yi = ring[i][1];
		const xj = ring[j][0];
		const yj = ring[j][1];
		const dy = yj - yi;
		if (dy === 0) continue;
		const intersect = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / dy + xi;
		if (intersect) inside = !inside;
	}
	return inside;
}

function pointInPolygonCoords(lng: number, lat: number, coords: number[][][]): boolean {
	if (!coords || coords.length === 0) return false;
	if (!pointInRing(lng, lat, coords[0])) return false;
	for (let h = 1; h < coords.length; h++) {
		if (pointInRing(lng, lat, coords[h])) return false;
	}
	return true;
}

function pointInGeometry(lng: number, lat: number, geom: any): boolean {
	if (!geom || !geom.type) return false;
	if (geom.type === 'Polygon') return pointInPolygonCoords(lng, lat, geom.coordinates);
	if (geom.type === 'MultiPolygon') {
		for (let p = 0; p < geom.coordinates.length; p++) {
			if (pointInPolygonCoords(lng, lat, geom.coordinates[p])) return true;
		}
	}
	return false;
}

// Signed area + centroid of a single ring (Green's theorem); used for the canonical county → state interior point.
function ringSignedAreaAndCentroid(
	ring: number[][]
): { signedArea: number; cx: number; cy: number } | null {
	if (!ring || ring.length < 3) return null;
	let signedAreaDoubled = 0;
	let cxAccum = 0;
	let cyAccum = 0;
	for (let i = 0; i < ring.length - 1; i++) {
		const x0 = ring[i][0];
		const y0 = ring[i][1];
		const x1 = ring[i + 1][0];
		const y1 = ring[i + 1][1];
		const cross = x0 * y1 - x1 * y0;
		signedAreaDoubled += cross;
		cxAccum += (x0 + x1) * cross;
		cyAccum += (y0 + y1) * cross;
	}
	if (signedAreaDoubled === 0) return null;
	return {
		signedArea: signedAreaDoubled * 0.5,
		cx: cxAccum / (3 * signedAreaDoubled),
		cy: cyAccum / (3 * signedAreaDoubled),
	};
}

function areaWeightedCentroid(geom: any): [number, number] | null {
	if (!geom || !geom.coordinates) return null;
	if (geom.type === 'Polygon') {
		const r = ringSignedAreaAndCentroid(geom.coordinates[0]);
		return r ? [r.cx, r.cy] : null;
	}
	if (geom.type === 'MultiPolygon') {
		let totalAbsArea = 0;
		let cxW = 0;
		let cyW = 0;
		for (let p = 0; p < geom.coordinates.length; p++) {
			const sub = geom.coordinates[p];
			if (!sub || !sub[0]) continue;
			const r = ringSignedAreaAndCentroid(sub[0]);
			if (!r) continue;
			const abs = Math.abs(r.signedArea);
			totalAbsArea += abs;
			cxW += r.cx * abs;
			cyW += r.cy * abs;
		}
		if (totalAbsArea === 0) return null;
		return [cxW / totalAbsArea, cyW / totalAbsArea];
	}
	return null;
}

// Interior point for bucketing: area-weighted centroid if inside the own polygon, else bbox center.
function resolveInteriorPoint(geom: any, bbox: AdmBbox | null): [number, number] | null {
	const centroid = areaWeightedCentroid(geom);
	if (centroid && pointInGeometry(centroid[0], centroid[1], geom)) {
		return centroid;
	}
	if (bbox) {
		return [(bbox.minLng + bbox.maxLng) / 2, (bbox.minLat + bbox.maxLat) / 2];
	}
	return null;
}

function collectUniqueIsosFromAreas(areas: ServiceArea[]): string[] {
	const isoSet = new Set<string>();
	for (let i = 0; i < areas.length; i++) {
		const group = areas[i] && areas[i].boundaryShapeGroup;
		if (group) {
			const iso = String(group).trim().toUpperCase();
			if (iso.length === 3) isoSet.add(iso);
		}
	}
	return Array.from(isoSet);
}

// Build per-ADM1 `core` (centroid-resident) and `fuzzy` ⊇ `core` (also bbox-center) county sets, with an ADM1 bbox pre-filter for speed.
function bucketAdm2FeaturesIntoAdm1(
	adm1Features: any[],
	adm2Features: any[]
): Map<string, Adm1Bucket> {
	const bucketsByAdm1ShapeId = new Map<string, Adm1Bucket>();
	for (let i = 0; i < adm1Features.length; i++) {
		const adm1Feature = adm1Features[i];
		if (!adm1Feature || !adm1Feature.properties || !adm1Feature.geometry) continue;
		const shapeType = String(adm1Feature.properties.shapeType || '').toUpperCase();
		if (shapeType !== 'ADM1') continue;
		const shapeId = adm1Feature.properties.shapeID;
		if (shapeId === undefined || shapeId === null) continue;
		bucketsByAdm1ShapeId.set(String(shapeId), {
			adm1ShapeId: String(shapeId),
			adm1Feature: adm1Feature,
			adm1Bbox: bboxFromGeometry(adm1Feature.geometry),
			coreAdm2ShapeIds: new Set<string>(),
			fuzzyAdm2ShapeIds: new Set<string>(),
		});
	}

	function pointIsInsideBucket(cx: number, cy: number, bucket: Adm1Bucket): boolean {
		if (bucket.adm1Bbox) {
			if (
				cx < bucket.adm1Bbox.minLng ||
				cx > bucket.adm1Bbox.maxLng ||
				cy < bucket.adm1Bbox.minLat ||
				cy > bucket.adm1Bbox.maxLat
			) {
				return false;
			}
		}
		return pointInGeometry(cx, cy, bucket.adm1Feature.geometry);
	}

	for (let i = 0; i < adm2Features.length; i++) {
		const adm2Feature = adm2Features[i];
		if (!adm2Feature || !adm2Feature.properties || !adm2Feature.geometry) continue;
		const shapeType = String(adm2Feature.properties.shapeType || '').toUpperCase();
		if (shapeType !== 'ADM2') continue;
		const adm2ShapeIdRaw = adm2Feature.properties.shapeID;
		if (adm2ShapeIdRaw === undefined || adm2ShapeIdRaw === null) continue;
		const adm2ShapeId = String(adm2ShapeIdRaw);
		const adm2Bbox = bboxFromGeometry(adm2Feature.geometry);
		if (!adm2Bbox) continue;

		// Canonical county → state test: area-weighted centroid, falling back to bbox center for degenerate shapes.
		const centroid = areaWeightedCentroid(adm2Feature.geometry);
		const centroidInsideOwn =
			centroid !== null && pointInGeometry(centroid[0], centroid[1], adm2Feature.geometry);
		const coreInteriorPoint: [number, number] | null = centroidInsideOwn
			? centroid
			: [(adm2Bbox.minLng + adm2Bbox.maxLng) / 2, (adm2Bbox.minLat + adm2Bbox.maxLat) / 2];

		// Fuzzy test: bbox center, matching the map's refinement pass so re-add flows are recognised.
		const bboxCenter: [number, number] = [
			(adm2Bbox.minLng + adm2Bbox.maxLng) / 2,
			(adm2Bbox.minLat + adm2Bbox.maxLat) / 2,
		];

		let assignedCoreBucket: Adm1Bucket | null = null;
		if (coreInteriorPoint) {
			for (const bucket of bucketsByAdm1ShapeId.values()) {
				if (pointIsInsideBucket(coreInteriorPoint[0], coreInteriorPoint[1], bucket)) {
					assignedCoreBucket = bucket;
					break;
				}
			}
		}
		if (assignedCoreBucket) {
			assignedCoreBucket.coreAdm2ShapeIds.add(adm2ShapeId);
			assignedCoreBucket.fuzzyAdm2ShapeIds.add(adm2ShapeId);
		}

		// All-buckets scan: a border county's bbox center may match multiple neighbour polygons.
		for (const bucket of bucketsByAdm1ShapeId.values()) {
			if (bucket === assignedCoreBucket) continue;
			if (pointIsInsideBucket(bboxCenter[0], bboxCenter[1], bucket)) {
				bucket.fuzzyAdm2ShapeIds.add(adm2ShapeId);
			}
		}
	}
	return bucketsByAdm1ShapeId;
}

function findCountryDisplayNameFromWorldAdm0(worldAdm0Features: any[], iso: string): string | null {
	for (let i = 0; i < worldAdm0Features.length; i++) {
		const f = worldAdm0Features[i];
		if (!f || !f.properties) continue;
		const g = String(f.properties.shapeGroup || f.properties.shapeISO || '').toUpperCase();
		if (g === iso) return f.properties.shapeName ? String(f.properties.shapeName) : null;
	}
	return null;
}

function findAdm0FeatureForIso(worldAdm0Features: any[], iso: string): any | null {
	for (let i = 0; i < worldAdm0Features.length; i++) {
		const f = worldAdm0Features[i];
		if (!f || !f.properties) continue;
		const g = String(f.properties.shapeGroup || f.properties.shapeISO || '').toUpperCase();
		if (g === iso) return f;
	}
	return null;
}

// Build a whole-state ServiceArea from its ADM1 feature.
function buildStateRowFromAdm1Feature(adm1Feature: any, countryDisplayName: string): ServiceArea {
	const props = adm1Feature.properties || {};
	const stateName = props.shapeName ? String(props.shapeName) : 'Unknown';
	const bbox = bboxFromGeometry(adm1Feature.geometry);
	const center: [number, number] = bbox
		? [(bbox.minLng + bbox.maxLng) / 2, (bbox.minLat + bbox.maxLat) / 2]
		: [0, 0];
	return {
		name: countryDisplayName ? stateName + ', ' + countryDisplayName : stateName,
		placeType: 'region',
		center: center,
		bbox: bbox ? [bbox.minLng, bbox.minLat, bbox.maxLng, bbox.maxLat] : null,
		all: false,
		boundaryShapeId: props.shapeID ? String(props.shapeID) : undefined,
		boundaryShapeGroup:
			props.shapeGroup || props.shapeISO ? String(props.shapeGroup || props.shapeISO) : undefined,
		boundaries: adm1Feature.geometry || undefined,
	} as ServiceArea;
}

function buildCountryRowFromAdm0Feature(adm0Feature: any): ServiceArea {
	const props = adm0Feature.properties || {};
	const countryName = props.shapeName ? String(props.shapeName) : 'Unknown';
	const bbox = bboxFromGeometry(adm0Feature.geometry);
	const center: [number, number] = bbox
		? [(bbox.minLng + bbox.maxLng) / 2, (bbox.minLat + bbox.maxLat) / 2]
		: [0, 0];
	return {
		name: countryName,
		placeType: 'country',
		center: center,
		bbox: bbox ? [bbox.minLng, bbox.minLat, bbox.maxLng, bbox.maxLat] : null,
		all: false,
		boundaryShapeId: props.shapeID ? String(props.shapeID) : undefined,
		boundaryShapeGroup:
			props.shapeGroup || props.shapeISO ? String(props.shapeGroup || props.shapeISO) : undefined,
		boundaries: adm0Feature.geometry || undefined,
	} as ServiceArea;
}

// Collapses fully-covered states / countries; returns the input reference when nothing changes. Never mutates.
export async function consolidateServiceAreasFromCdn(
	areas: ServiceArea[],
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	_getStateLabel?: LabelFn,
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	_getCountryLabel?: LabelFn
): Promise<ServiceArea[]> {
	if (!areas || areas.length < 2) {
		return areas;
	}
	const hasDistrict = areas.some((a) => a && a.placeType === 'district');
	const hasRegion = areas.some(
		(a) => a && (a.placeType === 'region' || (a as any).placeType === 'state')
	);
	if (!hasDistrict && !hasRegion) {
		return areas;
	}

	const countryIsos = collectUniqueIsosFromAreas(areas);
	if (countryIsos.length === 0) {
		return areas;
	}

	// Fetch world ADM0 + per-ISO ADM1/ADM2; reuses the cache primed by resolveServiceAreaBoundariesFromCDN.
	const fetchPromises: Promise<any | null>[] = [ensureCachedWorldAdm0FeatureCollection()];
	for (let i = 0; i < countryIsos.length; i++) {
		fetchPromises.push(ensureCachedGeoBoundariesFeatureCollection(countryIsos[i], 'ADM1'));
		if (hasDistrict) {
			fetchPromises.push(ensureCachedGeoBoundariesFeatureCollection(countryIsos[i], 'ADM2'));
		}
	}
	const fetched = await Promise.all(fetchPromises);
	const worldAdm0FeatureCollection = fetched[0];
	const worldAdm0Features: any[] =
		worldAdm0FeatureCollection && Array.isArray(worldAdm0FeatureCollection.features)
			? worldAdm0FeatureCollection.features
			: [];

	// Interleave: [worldAdm0, iso0-ADM1, (iso0-ADM2?), iso1-ADM1, (iso1-ADM2?), ...]
	const adm1FeaturesByIso: Record<string, any[]> = {};
	const adm2FeaturesByIso: Record<string, any[]> = {};
	let cursor = 1;
	for (let i = 0; i < countryIsos.length; i++) {
		const iso = countryIsos[i];
		const adm1Fc = fetched[cursor++];
		adm1FeaturesByIso[iso] =
			adm1Fc && Array.isArray(adm1Fc.features) ? (adm1Fc.features as any[]) : [];
		if (hasDistrict) {
			const adm2Fc = fetched[cursor++];
			adm2FeaturesByIso[iso] =
				adm2Fc && Array.isArray(adm2Fc.features) ? (adm2Fc.features as any[]) : [];
		} else {
			adm2FeaturesByIso[iso] = [];
		}
	}

	// Pass 1: district → region. Route each county to its core ADM1 (label-tiebreak across fuzzy homes) and collapse when `core ⊆ selected ⊆ fuzzy`.
	let workingAreas = areas.slice();

	for (let isoIndex = 0; isoIndex < countryIsos.length; isoIndex++) {
		const iso = countryIsos[isoIndex];
		const adm1Features = adm1FeaturesByIso[iso] || [];
		const adm2Features = adm2FeaturesByIso[iso] || [];
		if (adm1Features.length === 0 || adm2Features.length === 0) continue;
		const adm1Buckets = bucketAdm2FeaturesIntoAdm1(adm1Features, adm2Features);
		if (adm1Buckets.size === 0) continue;

		// ADM2 shapeID → canonical ADM1 (core) + every ADM1 that includes it (fuzzy).
		const adm2ToCoreAdm1: Map<string, string> = new Map();
		const adm2ToFuzzyAdm1s: Map<string, string[]> = new Map();
		for (const bucket of adm1Buckets.values()) {
			for (const adm2Id of bucket.coreAdm2ShapeIds) {
				adm2ToCoreAdm1.set(adm2Id, bucket.adm1ShapeId);
			}
			for (const adm2Id of bucket.fuzzyAdm2ShapeIds) {
				let list = adm2ToFuzzyAdm1s.get(adm2Id);
				if (!list) {
					list = [];
					adm2ToFuzzyAdm1s.set(adm2Id, list);
				}
				list.push(bucket.adm1ShapeId);
			}
		}

		// Lowercased ADM1 shapeName → shapeID, for routing ambiguous county rows by state label.
		const stateNameToAdm1ShapeId: Map<string, string> = new Map();
		for (const bucket of adm1Buckets.values()) {
			const props = bucket.adm1Feature && bucket.adm1Feature.properties;
			if (!props || !props.shapeName) continue;
			stateNameToAdm1ShapeId.set(String(props.shapeName).trim().toLowerCase(), bucket.adm1ShapeId);
		}

		// Group selected district rows (matching this ISO) by their resolved ADM1 bucket.
		const selectedByAdm1: Map<string, { indices: number[]; shapeIds: Set<string> }> = new Map();
		for (let areaIndex = 0; areaIndex < workingAreas.length; areaIndex++) {
			const area = workingAreas[areaIndex];
			if (!area || area.placeType !== 'district') continue;
			if (
				!area.boundaryShapeGroup ||
				String(area.boundaryShapeGroup).trim().toUpperCase() !== iso
			) {
				continue;
			}
			const shapeId = area.boundaryShapeId ? String(area.boundaryShapeId) : '';
			if (!shapeId) continue;

			let routedAdm1Id: string | undefined = adm2ToCoreAdm1.get(shapeId);
			const fuzzyHomes = adm2ToFuzzyAdm1s.get(shapeId) || [];
			// Fallback for rare degenerate geometries with no core home.
			if (!routedAdm1Id && fuzzyHomes.length > 0) {
				routedAdm1Id = fuzzyHomes[0];
			}
			// Row-label tiebreak: route ambiguous border counties to the state their label names.
			const labeledStateName = getStateFromArea(area).trim().toLowerCase();
			if (labeledStateName) {
				const labeledAdm1Id = stateNameToAdm1ShapeId.get(labeledStateName);
				if (labeledAdm1Id && fuzzyHomes.indexOf(labeledAdm1Id) >= 0) {
					routedAdm1Id = labeledAdm1Id;
				}
			}
			if (!routedAdm1Id) continue;

			let entry = selectedByAdm1.get(routedAdm1Id);
			if (!entry) {
				entry = { indices: [], shapeIds: new Set<string>() };
				selectedByAdm1.set(routedAdm1Id, entry);
			}
			entry.indices.push(areaIndex);
			entry.shapeIds.add(shapeId);
		}

		// Collapse buckets where `core ⊆ selected ⊆ fuzzy`; drop duplicate children if the state row already exists.
		const countryDisplayName = findCountryDisplayNameFromWorldAdm0(worldAdm0Features, iso) || '';
		const indicesToRemove: Set<number> = new Set();
		const insertsByFirstIndex: Map<number, ServiceArea> = new Map();
		for (const [adm1Id, entry] of selectedByAdm1) {
			const bucket = adm1Buckets.get(adm1Id);
			if (!bucket) continue;
			if (bucket.coreAdm2ShapeIds.size === 0) continue;

			// Core ⊆ selected: every canonical county must be present.
			let coversCore = true;
			for (const id of bucket.coreAdm2ShapeIds) {
				if (!entry.shapeIds.has(id)) {
					coversCore = false;
					break;
				}
			}
			if (!coversCore) continue;

			// Selected ⊆ fuzzy: no selected county from another state may have leaked in.
			let allSelectedAreFuzzyMembers = true;
			for (const id of entry.shapeIds) {
				if (!bucket.fuzzyAdm2ShapeIds.has(id)) {
					allSelectedAreFuzzyMembers = false;
					break;
				}
			}
			if (!allSelectedAreFuzzyMembers) continue;

			const alreadyHasStateRow = workingAreas.some(
				(a) =>
					a &&
					(a.placeType === 'region' || (a as any).placeType === 'state') &&
					a.boundaryShapeId &&
					String(a.boundaryShapeId) === adm1Id
			);
			if (alreadyHasStateRow) {
				for (let r = 0; r < entry.indices.length; r++) indicesToRemove.add(entry.indices[r]);
				continue;
			}
			const stateRow = buildStateRowFromAdm1Feature(bucket.adm1Feature, countryDisplayName);
			const insertAt = Math.min.apply(null, entry.indices);
			insertsByFirstIndex.set(insertAt, stateRow);
			for (let r = 0; r < entry.indices.length; r++) indicesToRemove.add(entry.indices[r]);
		}
		if (indicesToRemove.size > 0) {
			const next: ServiceArea[] = [];
			for (let i = 0; i < workingAreas.length; i++) {
				if (indicesToRemove.has(i)) {
					const insert = insertsByFirstIndex.get(i);
					if (insert) next.push(insert);
					continue;
				}
				next.push(workingAreas[i]);
			}
			workingAreas = next;
		}
	}

	// Pass 2: region → country. Collapse when every ADM1 for an ISO is selected; recomputed after Pass 1's new region rows.
	if (hasRegion || workingAreas.length !== areas.length) {
		for (let isoIndex = 0; isoIndex < countryIsos.length; isoIndex++) {
			const iso = countryIsos[isoIndex];
			const adm1Features = adm1FeaturesByIso[iso] || [];
			if (adm1Features.length === 0) continue;
			const allAdm1ShapeIds: Set<string> = new Set();
			for (let i = 0; i < adm1Features.length; i++) {
				const f = adm1Features[i];
				const shapeType =
					f && f.properties ? String(f.properties.shapeType || '').toUpperCase() : '';
				if (shapeType !== 'ADM1') continue;
				const id = f.properties.shapeID;
				if (id !== undefined && id !== null) allAdm1ShapeIds.add(String(id));
			}
			if (allAdm1ShapeIds.size === 0) continue;
			const selectedRegionIndices: number[] = [];
			const selectedRegionShapeIds: Set<string> = new Set();
			let alreadyHasCountryRow = false;
			for (let areaIndex = 0; areaIndex < workingAreas.length; areaIndex++) {
				const area = workingAreas[areaIndex];
				if (!area) continue;
				const group = area.boundaryShapeGroup
					? String(area.boundaryShapeGroup).trim().toUpperCase()
					: '';
				if (group !== iso) continue;
				if (area.placeType === 'country') {
					alreadyHasCountryRow = true;
					break;
				}
				if (area.placeType === 'region' || (area as any).placeType === 'state') {
					const sid = area.boundaryShapeId ? String(area.boundaryShapeId) : '';
					if (sid) {
						selectedRegionIndices.push(areaIndex);
						selectedRegionShapeIds.add(sid);
					}
				}
			}
			if (alreadyHasCountryRow) continue;
			if (selectedRegionShapeIds.size !== allAdm1ShapeIds.size) continue;
			let covers = true;
			for (const id of allAdm1ShapeIds) {
				if (!selectedRegionShapeIds.has(id)) {
					covers = false;
					break;
				}
			}
			if (!covers) continue;
			const countryFeature = findAdm0FeatureForIso(worldAdm0Features, iso);
			if (!countryFeature) continue;
			const countryRow = buildCountryRowFromAdm0Feature(countryFeature);
			const insertAt = Math.min.apply(null, selectedRegionIndices);
			const removeSet = new Set(selectedRegionIndices);
			const next: ServiceArea[] = [];
			for (let i = 0; i < workingAreas.length; i++) {
				if (removeSet.has(i)) {
					if (i === insertAt) next.push(countryRow);
					continue;
				}
				next.push(workingAreas[i]);
			}
			workingAreas = next;
		}
	}

	return workingAreas.length === areas.length && workingAreas.every((a, i) => a === areas[i])
		? areas
		: workingAreas;
}
